import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
  realtime: {
    params: { eventsPerSecond: 10 },
  },
});

// ─── Database Types ───────────────────────────────────────────

export type UserRole = 'super_admin' | 'principal' | 'teacher' | 'parent' | 'student';
export type SubscriptionStatus = 'pending_verification' | 'pending_payment' | 'active' | 'suspended';
export type AttendanceStatus = 'present' | 'absent' | 'late';
export type RankTier = 'top_25' | 'upper_mid' | 'lower_mid' | 'bottom_25';
export type AdmissionStatus = 'enquiry' | 'interview_scheduled' | 'accepted' | 'rejected' | 'enrolled';
export type NotificationType =
  | 'score_entered' | 'absent_marked' | 'fee_due' | 'event_posted'
  | 'tier_changed' | 'homework_missed' | 'subscription_overdue'
  | 'verification_approved' | 'verification_rejected' | 'school_activated'
  | 'announcement' | 'timetable_substitution' | 'event'
  | 'role_assigned' | 'role_removed';

export interface TimetablePeriod {
  id: string;
  school_id: string;
  class_id: string;
  day_of_week: number; // 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
  period_number: number;
  subject_name: string;
  teacher_id: string | null;
  start_time: string; // "HH:MM"
  duration_minutes: number;
  is_recess: boolean;
  created_at: string;
  updated_at: string;
  // joined
  teacher?: { full_name: string };
  class?: { name: string };
}

export interface TimetableSubstitution {
  id: string;
  school_id: string;
  period_id: string;
  original_teacher_id: string | null;
  substitute_teacher_id: string;
  substitution_date: string;
  assigned_by: string | null;
  created_at: string;
  // joined
  substitute_teacher?: { full_name: string };
  period?: TimetablePeriod;
}

export interface School {
  id: string;
  edtrack_school_id: string | null;
  name: string;
  address: string | null;
  contact_number: string | null;
  registration_number: string;
  logo_url: string | null;
  subscription_status: SubscriptionStatus;
  subscription_start: string | null;
  verified: boolean;
  verified_at: string | null;
  setup_complete: boolean;
  activated: boolean;
  theme_color: string | null;
  color_palette: import('./extractSchoolPalette').SchoolPalette | null;
  created_at: string;
  // Live billing model — set per-school in the admin panel. monthly_fee is
  // NOT NULL at the DB level (default 0 means "not set"); flat_fee_override
  // and the trial dates are nullable.
  monthly_fee: number;
  flat_fee_override: number | null;
  trial_start_date: string | null;
  trial_end_date: string | null;
  // Under-13 account restriction — classes with grade_level >= this value
  // get student logins; classes below it don't. null/undefined = no
  // restriction (every class gets accounts, today's default behavior).
  account_restriction_cutoff: number | null;
  // Nigeria Expansion, Phase A (Blueprint Section 1). Defaults to
  // 'pakistan' at the DB level, so existing schools need no backfill and
  // every country-gated feature stays off for them automatically.
  country: 'pakistan' | 'nigeria';
  // Nigeria only — which class-label convention the admin picked during
  // verification. Null for Pakistan and for any Nigerian school not yet
  // set (falls back to 'nigerian_standard' via getNigeriaClassTemplates()).
  class_naming_scheme: 'nigerian_standard' | 'international_grade' | null;
  // Nigeria expansion, Phase B3 (Blueprint Section 3). Null = fall back to
  // the hardcoded 33%-pass scale in reportEngine.ts's DEFAULT_GRADING_SCALE.
  grading_scale: { grade: string; min_percent: number; remark: string }[] | null;
}

export interface User {
  id: string;
  school_id: string | null;
  role: UserRole;
  full_name: string;
  email: string | null;
  phone: string | null;
  username: string;
  default_password_changed: boolean;
  avatar_url: string | null;
  is_active: boolean;
  // Login-gating only — separate from is_active (which already controls
  // roster/list visibility in ~47 places across the app). A student with
  // login_suspended = true still shows up everywhere normally; they just
  // can't sign in. Only ever set for students, via the age-restriction
  // cutoff — never touched for teachers/principals/parents.
  login_suspended: boolean;
  created_at: string;
}

export interface Class {
  id: string;
  school_id: string;
  name: string;
  grade_level: number;
  teacher_id: string | null;
  academic_year: string;
  is_active: boolean;
  // Nigeria only (Blueprint Section 4). Null for every Pakistani class —
  // preserves today's pure grade_level-as-position ordering for them
  // exactly. 0=Kindergarten 1=Nursery 2=Primary 3=Highschool. Sort by
  // `tier NULLS FIRST, grade_level` everywhere classes are listed.
  tier: number | null;
}

export interface Student {
  id: string;
  school_id: string;
  class_id: string;
  user_id: string | null;
  parent_id: string | null;
  secondary_parent_phone: string | null;
  roll_number: string | null;
  date_of_birth: string | null;
  gender: 'male' | 'female' | 'other' | null;
  profile_photo: string | null;
  is_active: boolean;
  // Joined fields
  full_name?: string;
  class_name?: string;
  has_account?: boolean;
}

export interface Subject {
  id: string;
  school_id: string;
  class_id: string;
  name: string;
  teacher_id: string | null;
  total_marks: number;
  is_active: boolean;
  // Nigeria only, Secondary tier (Blueprint Section 6). Independent of
  // is_selective — a subject can be Core+Compulsory, Core+Selective,
  // Shared+Compulsory, or Shared+Selective. Null everywhere else.
  scope: 'core' | 'shared' | null;
  // Subjects sharing this id are a "choose exactly one" group (e.g.
  // Biology/Agriculture). Null = compulsory, or a standalone optional
  // elective. Blueprint Section 6.
  selective_group_id: string | null;
}

export interface TestScore {
  id: string;
  school_id: string;
  student_id: string;
  subject_id: string;
  test_name: string;
  marks_obtained: number;
  total_marks: number;
  test_date: string;
  entered_by: string | null;
}

export interface Attendance {
  id: string;
  school_id: string;
  student_id: string;
  class_id: string;
  date: string;
  status: AttendanceStatus;
  marked_by: string | null;
}

export interface Ranking {
  id: string;
  school_id: string;
  student_id: string;
  class_id: string;
  term: string;
  total_score: number;
  rank_position: number | null;
  rank_tier: RankTier | null;
  attendance_percentage: number;
  homework_percentage: number;
  test_average: number;
  updated_at: string;
}

export interface Fee {
  id: string;
  school_id: string;
  student_id: string;
  amount: number;
  due_date: string;
  status: 'paid' | 'pending' | 'overdue';
  paid_date: string | null;
  description: string;
}

export interface Notification {
  id: string;
  school_id: string | null;
  user_id: string;
  title: string;
  body: string;
  type: NotificationType;
  read: boolean;
  created_at: string;
}

export interface Announcement {
  id: string;
  school_id: string;
  title: string;
  body: string;
  sent_to: string[];
  sent_by: string | null;
  recipient_count: number;
  created_at: string;
}

export type NoticeCategory = 'notice' | 'announcement';
export type NoticeBehavior = 'acknowledge' | 'link' | 'persistent_version';

export interface AdminNotice {
  id: string;
  type: NoticeCategory;
  behavior: NoticeBehavior;
  title: string;
  body: string;
  cta_label: string | null;
  cta_url: string | null;
  target_version: string | null; // only set when behavior = 'persistent_version'
  target_school_ids: string[] | null; // null = every school
  scheduled_for: string | null;
  sent_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface NoticeRead {
  id: string;
  notice_id: string;
  user_id: string;
  seen_at: string;
  acknowledged_at: string | null;
}

export interface AdminSession {
  id: string;
  admin_id: string;
  device_label: string | null;
  ip_address: string | null;
  city: string | null;
  country: string | null;
  user_agent: string | null;
  trusted: boolean;
  is_suspicious: boolean;
  last_active_at: string;
  created_at: string;
  revoked_at: string | null;
}

export interface Subscription {
  id: string;
  school_id: string;
  student_count: number;
  amount_due: number;
  amount_paid: number | null;
  payment_method: 'jazzcash' | 'easypaisa' | 'bank' | null;
  period_start: string;
  period_end: string;
  due_date: string;
  status: 'paid' | 'pending' | 'overdue';
  receipt_url: string | null;
}

export interface Admission {
  id: string;
  school_id: string;
  student_name: string;
  parent_name: string;
  contact_number: string;
  grade_applying_for: number;
  status: AdmissionStatus;
  interview_date: string | null;
  notes: string | null;
  converted_student_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Event {
  id: string;
  school_id: string;
  title: string;
  details: string | null;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  audience: string[];
  is_day_off: boolean;
  created_by: string | null;
}

export interface ExamPlanner {
  id: string;
  school_id: string;
  class_id: string;
  subject_id: string | null;
  title: string;
  exam_date: string;
  duration: number | null;
  venue: string | null;
}

export interface SyllabusTopic {
  id: string;
  school_id: string;
  subject_id: string;
  topic_name: string;
  order_index: number;
}

export interface CourseCoverage {
  id: string;
  school_id: string;
  subject_id: string;
  topic_id: string;
  teacher_id: string | null;
  covered_on: string;
  notes: string | null;
}

export interface QuizQuestion {
  id: string;
  school_id: string;
  subject_id: string;
  grade_level: number;
  year: number | null;
  term: string | null;
  topic: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: 'a' | 'b' | 'c' | 'd';
  marks: number;
}

export interface PastPaperFile {
  id: string;
  school_id: string;
  subject_id: string | null;
  grade_level: number;
  year: number;
  term: string | null;
  file_url: string;
  file_type: 'pdf' | 'image' | 'doc';
  title: string;
  uploaded_at: string;
}

export interface GlobalParent {
  id: string;
  full_name: string;
  phone: string;
  secondary_phone: string | null;
  created_at: string;
}

// Nigeria Terms fix. A term is now its own thing the principal creates and
// names directly on the Report page — no longer inferred from, or tied to,
// any exam_events row. Reports generated "for a term" pull their date
// window (exams, attendance, class tests — everything that already pulls
// by date range) from start_date/end_date here instead.
export interface Term {
  id: string;
  school_id: string;
  name: string;
  start_date: string;
  end_date: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ParentStudentJunction {
  id: string;
  parent_id: string;
  student_id: string;
  school_id: string;
  created_at: string;
}